import { Injectable, inject } from '@angular/core';

import { Content } from '@23degrees/shared-code/models';
import { MessageBus } from '@23degrees/shared/bus';
import { ContentService } from '@23degrees/shared/services/content';
import { ShareService } from '@23degrees/shared/services/share';
import { SPINNER_OVERLAY_MESSAGES } from '@config';
import { transaction } from '@datorama/akita';
import { NotificationsService } from 'angular2-notifications';
import { throwError } from 'rxjs';
import { catchError, finalize, map, take, tap } from 'rxjs/operators';
import { FavoritesQuery } from '../favorites/favorites.query';
import { FavoritesStoreService } from '../favorites/favorites.service';
import { CollectionsStore } from './collections.store';

const pauseToast = {
  timeOut: 3000,
  showProgressBar: true,
  pauseOnHover: true,
  clickToClose: true,
};

@Injectable({ providedIn: 'root' })
export class CollectionsStoreService {
  private collectionsStore = inject(CollectionsStore);
  private favoritesStoreService = inject(FavoritesStoreService);
  private favoritesQuery = inject(FavoritesQuery);
  private contentService = inject(ContentService);
  private shareService = inject(ShareService);
  private notificationService = inject(NotificationsService);
  private messageBus = inject(MessageBus);

  itemsPerPage: number = 100;

  deletedContent: Content;
  deletedChildContent: Content;
  wasFavorized: boolean = false;

  setActive(id: String) {
    // console.log('[CollectionStoreService] - set active ');
    this.collectionsStore.setActive(id);
  }

  async get() {
    // console.log('[CollectionStoreService] - get all collections ');
    let page = 0;
    let stop = false;
    let entities = [];
    while (!stop) {
      try {
        // gets root folders only
        const newEntities = await this.contentService
          .getContentList(page, this.itemsPerPage, 'updated_at desc', 'myandshared', 'foldershortcuts')
          .pipe(
            map((response) => response.payload),
            map((folders) => {
              folders.forEach((folder) => {
                if (!folder.fileName) folder.fileName = folder.name;
              });
              return folders;
            }),
          )
          .toPromise();

        if (newEntities.length < this.itemsPerPage) {
          stop = true;
        }
        entities = entities.concat(newEntities);
        page++;
      } catch (error) {
        stop = true;
      }
    }
    this.collectionsStore.set(entities);
  }

  add(content: Content) {
    const add$ = this.contentService.createContent(content).pipe(
      take(1),
      tap((response) => {
        setTimeout(() => {
          // Give elastic time to sync
          this.collectionsStore.add(response.data, { prepend: true });
          this.setActive(response.data._id);
          this.notificationService.success('Created new folder', content.fileName || content.name);
        }, 1000);
      }),
      catchError((err) => {
        this.notificationService.error('Something went wrong!', 'Could not create new folder');
        return throwError(err);
      }),
    );

    return add$;
  }

  push(content: Content) {
    if (content.type === 'collection') {
      this.collectionsStore.add(content);
    }
  }

  @transaction()
  update(content: Content) {
    this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.ShowSpinnerOverlay, ['spinneroverlay'], {
      type: 'default',
      default: {
        content: content,
        preventOverlayClosing: true,
        showEmbed: false,
        loadingInfo: 'Update',
      },
    });
    //@manfred directly calling v1 update is a very dangerous thing to do
    //you do not know if you get a full content item this way
    //if you call this with a map that was queried as card
    //this destroys the content item in v1
    console.log('[UpdateContent] - collection service store', content);
    const update$ = this.contentService
      .updateContent(content)
      .pipe(
        take(1),
        tap(async (response) => {
          this.wasFavorized = this.favoritesQuery.hasEntity(content._id);
          if (this.wasFavorized) {
            this.favoritesStoreService.update(content);
          }
          //this.collectionsStore.update(content._id, response.data);
          //reload everyhting since sub collections can have new sharing
          await this.get();
          this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
          this.notificationService.success('Updated collection', content.fileName || content.name);
        }),
        catchError((err) => {
          this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
          this.notificationService.error('Something went wrong!', 'Could not update collection');
          return throwError(err);
        }),
      )
      .subscribe();

    return update$;
  }

  @transaction()
  remove(content: Content) {
    const remove$ = this.contentService.deleteContent(content._id).pipe(
      take(1),
      tap(async () => {
        this.wasFavorized = this.favoritesQuery.hasEntity(content._id);
        if (this.wasFavorized) {
          this.favoritesStoreService.remove(content, true);
        }
        await this.get();
      }),
    );

    remove$.subscribe((response) => {
      this.deletedContent = response.data;

      // Give elastic time to sync
      setTimeout(() => {
        this.collectionsStore.remove(this.deletedContent._id);

        const toast = this.notificationService.success('Deleted collection', content.fileName || content.name, pauseToast);
        toast.click.pipe(take(1)).subscribe((event) => {
          this.restoreCollection();
        });
      }, 1000);
    });

    return remove$;
  }

  @transaction()
  restoreCollection() {
    this.contentService
      .restoreContent(this.deletedContent._id, this.deletedContent.deleted_at)
      .pipe(
        take(1),
        tap(() => {
          this.notificationService.success('Restored collection', this.deletedContent.fileName || this.deletedContent.name, pauseToast);
        }),
      )
      .subscribe((response) => {
        this.collectionsStore.add(response.data);

        if (this.wasFavorized) {
          this.favoritesStoreService.add(response.data, true);
        }

        this.deletedContent = null;
        this.wasFavorized = false;
      });
  }

  @transaction()
  removeItemFromCollection(content) {
    const remove$ = this.contentService.deleteContent(content._id).pipe(
      take(1),
      tap(() => {
        this.wasFavorized = this.favoritesQuery.hasEntity(content._id);
        if (this.wasFavorized) {
          this.favoritesStoreService.remove(content, true);
        }
      }),
    );

    remove$.subscribe((response) => {
      this.deletedChildContent = response.data;

      const toast = this.notificationService.success('Deleted Item', content.fileName || content.name, pauseToast);
      toast.click.pipe(take(1)).subscribe((event) => {
        this.restoreItemInCollection();
      });
    });

    return remove$;
  }

  @transaction()
  restoreItemInCollection() {
    console.log('RESTORING ITEM IN COLLECTION', this.deletedChildContent);
    this.contentService
      .restoreContent(this.deletedChildContent._id, this.deletedChildContent.deleted_at)
      .pipe(
        take(1),
        tap(() => {
          this.notificationService.success('Restored item', this.deletedChildContent.fileName || this.deletedChildContent.name, pauseToast);
        }),
      )
      .subscribe((response) => {
        if (this.wasFavorized) {
          this.favoritesStoreService.add(response.data, true);
        }

        this.deletedChildContent = null;
        this.wasFavorized = false;
      });
  }

  @transaction()
  moveItemToCollection(collection, content, silent: boolean = false) {
    this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.ShowSpinnerOverlay, ['spinneroverlay'], {
      type: 'default',
      default: {
        content: content,
        preventOverlayClosing: true,
        showEmbed: false,
        loadingInfo: 'Update',
      },
    });
    console.log('[CollectionStoreService] - moveItemToCollection');
    return this.contentService.moveContent(content.slug, collection.slug).pipe(
      //return this.collectionsService.addContentToCollection(collection._id, content._id).pipe(
      take(1),
      tap(async () => {
        if (!silent) {
          this.notificationService.success('Moved to ' + collection.fileName || collection.name, content.fileName || content.name);
        }
        //get this content to get the latest version
        const newContent = await this.contentService.getContentBySlugPromise(content.slug);
        console.log('[newContent] ', newContent);
        this.collectionsStore.update(collection._id, { items: [newContent] });
        this.favoritesStoreService.updateContent(collection._id, { items: [newContent] });

        const realContent = content.type === 'shortcut' ? content.typeSpecific.shortcutContent : content;

        if (realContent.type === 'collection') {
          // console.log('moved to ', collection.fileName, content.fileName);
          this.collectionsStore.update(content._id, { folderSlug: collection.slug });
          await this.get();
        }
      }),
      finalize(() => {
        this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
      }),
    );
  }

  @transaction()
  saveItemToCollection(content: Content, folder: Content, silent: boolean = false) {
    return this.contentService.cloneItem(content.slug, folder?.slug || null).pipe(
      take(1),
      tap((response) => {
        console.log('[CollectionsStoreService][saveItemToCollection]', response, content, folder);
        // const { content, collection, contentToContent } = response.data;
        if (!silent) {
          this.notificationService.success('Saved to ' + folder.fileName || folder.name, content.fileName || content.name);
        }

        // console.log('saved to ', collection, content, contentToContent);
        this.collectionsStore.update(folder._id, { items: [content] });
        this.favoritesStoreService.updateContent(folder._id, { items: [content] });
      }),
    );
  }

  @transaction()
  createShortcut(content: Content, folder: Content, fileName: string, silent: boolean = false) {
    return this.contentService.createShortcut(content, folder, fileName).pipe(
      take(1),
      tap(async (response) => {
        if (!silent) {
          this.notificationService.success('Created Shortcut', (fileName || content.fileName || content.name) + ' in ' + folder.fileName || folder.name);
        }
        this.collectionsStore.add(response.payload, { prepend: true });

        // if (content.type === 'collection') {
        //   await this.get();
        // }
      }),
    );
  }

  async updateSharing(collectionId, shares, shareCoding) {
    this.collectionsStore.update(collectionId, { shares: shares, shareCoding: shareCoding });
  }
}
